/**
 * SEAM S1 — the alerts wire contract, IMPORTED from the api zod schemas, never
 * transcribed (slice 12's convention, the only seam `tsc` can enforce). If either
 * side invented, renamed or dropped a field, `tsc` fails here.
 *
 * THE ABSENCE THAT IS THE DESIGN (PLAN §6). `Alert` carries `certainty`,
 * `observedAt`, `priorAt`, `gapSec` and the two fixes' coordinates — and NO
 * `crossedAt`, NO bare `at`, NO bare `time`. A feed that knew only one timestamp
 * would force a crossing instant to be fabricated upstream. That absence is
 * enforced structurally by `keyAbsence.test.ts` (the web half) and by
 * `api/.../alerts/routes.test.ts` (the api half) — not by this comment.
 *
 * `import type` only: `verbatimModuleSyntax` erases these at build, so no api
 * module graph (hono, zod, drizzle) reaches the web bundle.
 */
import type {
  Alert,
  AlertsResponse as AlertsResponseApi,
  OverspeedAlert,
  MaintenanceAlert,
  TrackerHealthAlert,
  AfterHoursAlert,
  PowerAlert,
  IgnitionAlert,
  TamperAlert,
  IdleAlert,
  DataDelayedAlert,
  MovedWhileParkedAlert,
} from '../../../../api/src/modules/alerts/routes';

export type {
  Alert,
  OverspeedAlert,
  MaintenanceAlert,
  TrackerHealthAlert,
  AfterHoursAlert,
  PowerAlert,
  IgnitionAlert,
  TamperAlert,
  IdleAlert,
  DataDelayedAlert,
  MovedWhileParkedAlert,
};

/**
 * 🔴 SLICE D1 — the canonical web FEED UNION, with `driver` DISTRIBUTED across the
 * members as an optional field. It is DEFINED in `maintenance/types.ts` (the lowest
 * feed module — this file re-exports that module's narrowings, so defining it here
 * would be a cycle) and re-exported here, the historical home of `FeedItem`. See
 * that file for why the api's intersection shape could not be used directly.
 */
import type { FeedItem } from '@/features/maintenance/types';
export type { FeedItem };

/**
 * The feed response, with its `alerts` re-typed to the web's narrowable `FeedItem`
 * (the api envelope types them as the non-narrowable intersection). Everything else
 * — `serverTime`, `hasMore`, `nextCursor` — is the api's, unchanged.
 */
export type AlertsResponse = Omit<AlertsResponseApi, 'alerts'> & { alerts: FeedItem[] };

/**
 * 🔴 SLICE 28 — THE FEED UNION HAS A THIRD LEG, AND IT HAS NO COORDINATES.
 * `isMaintenance` and `hasObservedPosition` live in
 * `features/maintenance/types.ts` beside the row that renders them, and are
 * re-exported here so every existing feed consumer keeps ONE import for its
 * narrowings. A maintenance reminder did not happen anywhere: it carries no
 * `observedLat`/`observedLon`, and `hasObservedPosition` is what every map and
 * deep-link call site asks before reaching for them, so `tsc` proves the check at
 * each site rather than a comment asking for it.
 */
export {
  isMaintenance,
  isTrackerHealth,
  isPower,
  isIgnition,
  isTamper,
  isIdle,
  isDataDelayed,
  isDriverUnlink,
  isDriverAbsent,
  isSos,
  powerPin,
  ignitionPin,
  tamperPin,
  idlePin,
  sosPin,
  hasObservedPosition,
} from '@/features/maintenance/types';
export type { PositionedFeedItem, DriverUnlinkAlert, DriverAbsentAlert } from '@/features/maintenance/types';

/**
 * 🔴 SLICE 19 — the feed is a discriminated UNION. A geofence crossing has no
 * `type` key (the historical shape, byte-identical); an overspeed episode is
 * `type: 'overspeed'`. This is the one narrowing every feed consumer runs — the
 * list rows, the map, the CSV, the day summary — so it lives once, here, pinned to
 * the wire union rather than re-tested inline with a stringly `'type' in x` each
 * time. The api projects the discriminant (`toWire`, seam S1); the web never
 * invents it.
 */
export function isOverspeed(item: FeedItem): item is OverspeedAlert {
  return 'type' in item && item.type === 'overspeed';
}

/**
 * 🔴 THE FIFTH LEG OF THE FEED UNION (slice 37). An after-hours use —
 * `type: 'after_hours_use'`. Unlike maintenance and tracker-health it HAS
 * `observedLat`/`observedLon` (the anchor fix where the vehicle drove beyond 200 m
 * or turned its ignition on outside working hours), so it is a positioned item:
 * `hasObservedPosition` narrows it in via `PositionedFeedItem` with no change here,
 * and the map centres on it exactly like an overspeed or geofence point.
 *
 * Named positively, pinned to the wire union — the same discipline `isOverspeed`
 * and the maintenance/tracker narrowings follow, so the list rows, the map and the
 * day summary cannot each re-test it with a slightly different stringly check.
 */
export function isAfterHours(item: FeedItem): item is AfterHoursAlert {
  return 'type' in item && item.type === 'after_hours_use';
}

/**
 * 🔴 SLICE 82a — the ELEVENTH leg of the feed union: a MOVED-WHILE-PARKED alert
 * (`type: 'moved_while_parked'`). A vehicle parked ≥ 15 min then moved ≥ 50 m with
 * ignition KNOWN-ON at the moved-to fix — a security signal, `severity: 'warning'`.
 *
 * 🔴 UNLIKE the power/ignition/tamper/idle pin legs, it is a POSITIONED item: its
 * `observedLat`/`observedLon` are the MOVED-TO fix and are NON-NULL (the deep link
 * opens where the vehicle went). It therefore STAYS in `PositionedFeedItem` — it is
 * not added to the `Exclude` list in `features/maintenance/types.ts`, so
 * `hasObservedPosition` narrows it in and the map centres on its observed point,
 * exactly like overspeed and after-hours. Named positively, pinned to the wire union.
 */
export function isMovedWhileParked(item: FeedItem): item is MovedWhileParkedAlert {
  return 'type' in item && item.type === 'moved_while_parked';
}

/**
 * The GEOFENCE leg — the only shape with a `geofenceId`. Named positively rather
 * than as `!isOverspeed(...)`, because that negation silently became WRONG the
 * moment a third leg joined the union: it would have counted maintenance
 * reminders as zone visits, in a tally whose whole point is that it must not be
 * inflated by an event that touched no zone. `tsc` caught it here; the lesson is
 * that a narrowing spelled as "not the other one" is a claim about the size of a
 * union, and unions grow.
 */
export function isGeofenceCrossing(item: FeedItem): item is Alert {
  return !('type' in item);
}

/**
 * SEVERITY — purpose sets severity, not visibility (Mahad's ruling). Every crossing
 * renders in the feed; purpose decides which DIRECTION is the alert:
 *   - `allowed`   → exit = alert, entry = normal ("returned")
 *   - `restricted`→ entry = alert, exit = normal ("cleared")
 *
 * This is authored on the WIRE (`AlertZ.severity`, seam S1) and DERIVED there at
 * read time, because `purpose` is mutable — a stored severity would be a lie the
 * moment the fence's purpose flipped. The client therefore NEVER re-derives it; it
 * reads `alert.severity` straight from the feed, so client and server can never
 * disagree. `AlertSeverity` is pinned to the wire enum, not transcribed. */
export type AlertSeverity = Alert['severity'];

/** The three feed filter chips (Mahad's ruling) — All · Alerts · Unread.
 *  `alerts` is SERVER-SIDE (`?severity=alert`), never a client filter over a keyset
 *  page, which would show fewer rows than a page and break `hasMore`. Deliberately
 *  NOT the canvas 1o Critical/Fuel/Security chips (controls for features that do not
 *  exist). */
export type AlertFilter = 'all' | 'alerts' | 'unread';
